<script>
    import {
        processInspectionStore,
        appStateStore,
        inspectionWaitingStore,
        inspectionClassStore,
    } from "./store.js";
    import { onMount, createEventDispatcher } from "svelte";
    import toast from "svelte-french-toast";

    const dispatch = createEventDispatcher();

    let contentList;
    let isOpen = false;

    onMount(() => {
        if (contentList && $processInspectionStore.scrollPos > 0) {
            setTimeout(() => {
                contentList.scrollTop = $processInspectionStore.scrollPos;
            }, 100);
        }
    });

    function goToDetail(id) {
        const item = $processInspectionStore.list.find((i) => i.id === id);
        processInspectionStore.update((s) => ({
            ...s,
            selectedItem: item,
            scrollPos: contentList.scrollTop,
        }));
        appStateStore.set({ currentView: "detail", selectedId: id });
    }

    function goToNew() {
        if (!$inspectionClassStore.selectedCode) {
            toast.error("검사분류를 먼저 선택해주세요.");
            return;
        }
        inspectionWaitingStore.update((s) => ({ ...s, modalOpen: true }));
    }

    function openScanner() {
        if (!$inspectionClassStore.selectedCode) {
            toast.error("검사분류를 먼저 선택해주세요.");
            return;
        }
        dispatch("openScanner");
    }

    function selectClassification(code) {
        inspectionClassStore.update((s) => ({ ...s, selectedCode: code }));
        isOpen = false;
    }

    function getSelectedClassName(code) {
        const found = $inspectionClassStore.list.find(
            (cls) => cls.INSPCT_CL_CODE === code,
        );
        return found ? found.INSPCT_CL_NM.replaceAll("/", " > ") : "";
    }

    function formatPathHtml(name) {
        if (!name) return "";
        const parts = name.split("/");
        if (parts.length === 0) return "";
        if (parts.length === 1)
            return `<strong class="path-last">${parts[0]}</strong>`;
        const parents = parts.slice(0, -1).join(" &gt; ");
        const last = parts[parts.length - 1];
        return `<span class="path-parent">${parents} &gt; </span><strong class="path-last">${last}</strong>`;
    }

    function handleKeydown(e) {
        if (e.key === "Escape") {
            isOpen = false;
        }
    }
</script>

<svelte:window on:click={() => (isOpen = false)} on:keydown={handleKeydown} />

<section class="pane master-box">
    <div class="pane-header">
        <h2>
            📦 주 내역
            <span class="total-badge">
                총 {$processInspectionStore.list.length}건
            </span>
        </h2>
        <div class="header-actions">
            <div class="custom-dropdown">
                <button
                    class="dropdown-trigger"
                    class:active={isOpen}
                    on:click|stopPropagation={() => (isOpen = !isOpen)}
                >
                    <div class="trigger-content">
                        <svg
                            class="trigger-icon-svg"
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        >
                            <line x1="8" y1="6" x2="21" y2="6"></line>
                            <line x1="8" y1="12" x2="21" y2="12"></line>
                            <line x1="8" y1="18" x2="21" y2="18"></line>
                            <line x1="3" y1="6" x2="3.01" y2="6"></line>
                            <line x1="3" y1="12" x2="3.01" y2="12"></line>
                            <line x1="3" y1="18" x2="3.01" y2="18"></line>
                        </svg>
                        {#if $inspectionClassStore.selectedCode}
                            <span class="trigger-label">
                                {getSelectedClassName($inspectionClassStore.selectedCode)}
                            </span>
                        {:else}
                            <span class="trigger-label placeholder">
                                -- 검사분류 선택 --
                            </span>
                        {/if}
                    </div>
                    <span class="trigger-arrow">▼</span>
                </button>
                {#if isOpen}
                    <!-- svelte-ignore a11y-click-events-have-key-events -->
                    <!-- svelte-ignore a11y-no-static-element-interactions -->
                    <div class="dropdown-menu" on:click|stopPropagation>
                        <div class="menu-list-container">
                            {#if $inspectionClassStore.list.length === 0}
                                <div class="no-results">
                                    검색 결과가 없습니다.
                                </div>
                            {:else}
                                {#each $inspectionClassStore.list as cls}
                                    <button
                                        class="menu-item"
                                        class:selected={$inspectionClassStore.selectedCode === cls.INSPCT_CL_CODE}
                                        on:click={() => selectClassification(cls.INSPCT_CL_CODE)}
                                    >
                                        <div class="item-content">
                                            <div class="item-path">
                                                {@html formatPathHtml(cls.INSPCT_CL_NM)}
                                            </div>
                                            <div class="item-code">
                                                {cls.INSPCT_CL_CODE}
                                            </div>
                                        </div>
                                        {#if $inspectionClassStore.selectedCode === cls.INSPCT_CL_CODE}
                                            <span class="selected-checkmark">✓</span>
                                        {/if}
                                    </button>
                                {/each}
                            {/if}
                        </div>
                    </div>
                {/if}
            </div>
            <button class="back-link btn-scan" on:click={openScanner}>🔍 SCAN</button>
            <button class="back-link btn-new" on:click={goToNew}>+ 신규등록</button>
        </div>
    </div>
    <div class="table-scroll" bind:this={contentList}>
        {#if $processInspectionStore.list.length === 0}
            <div class="empty-state">
                <div class="empty-icon">📭</div>
                <p class="empty-text">검색 결과가 없습니다.</p>
            </div>
        {:else}
            <table class="mes-table">
                <thead>
                    <tr>
                        <th>No</th>
                        <th>생산계획번호</th>
                        <th>회차</th>
                        <th>LOT NO</th>
                        <th>ERP LOT NO</th>
                        <th>품목코드</th>
                        <th>품목명</th>
                        <th>규격</th>
                        <th>제조번호</th>
                        <th>검사일자</th>
                        <th>검사원</th>
                        <th>판정</th>
                        <th>조치여부</th>
                    </tr>
                </thead>
                <tbody>
                    {#each $processInspectionStore.list as item, i (item.id)}
                        <tr
                            on:click={() => goToDetail(item.id)}
                            class:selected={$processInspectionStore.selectedItem?.id === item.id}
                        >
                            <td class="center">{i + 1}</td>
                            <td>{item.prdctn_plan_no}</td>
                            <td class="center">{item.odr}</td>
                            <td class="bold">{item.lot_no}</td>
                            <td>{item.wrhousng_lot_no}</td>
                            <td>{item.item_code}</td>
                            <td>{item.item_nm}</td>
                            <td>{item.stndrd}</td>
                            <td>{item.mnfctur_no}</td>
                            <td class="center">{item.date}</td>
                            <td class="center">{item.korean_nm}</td>
                            <td class="center">
                                <span
                                    class="badge {item.status === '적합'
                                        ? 'badge-success'
                                        : item.status === '부적합'
                                          ? 'badge-danger'
                                          : 'badge-warning'}"
                                >
                                    {item.status}
                                </span>
                            </td>
                            <td class="center">
                                <span
                                    class="badge {item.action_result === '조치완료'
                                        ? 'badge-success'
                                        : item.action_result === '조치중'
                                          ? 'badge-warning'
                                          : 'badge-danger'}"
                                >
                                    {item.action_result || (item.managt_at === "Y" ? "조치완료" : "미조치")}
                                </span>
                            </td>
                        </tr>
                    {/each}
                </tbody>
            </table>
        {/if}
    </div>
</section>

<style>
    .pane {
        background: white;
        border-radius: 10px;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        height: 100%;
    }
    .pane-header {
        padding: 0 16px;
        background: #f8fafc;
        border-bottom: 1px solid #f1f5f9;
        display: flex;
        justify-content: space-between;
        align-items: center;
        height: 48px;
        flex-shrink: 0;
    }
    .pane-header h2 {
        font-size: 0.9rem;
        margin: 0;
        font-weight: 800;
        color: #475569;
        display: flex;
        align-items: center;
        gap: 8px;
    }
    .total-badge {
        font-size: 0.75rem;
        background: #e2e8f0;
        color: #475569;
        padding: 2px 8px;
        border-radius: 12px;
        font-weight: 700;
    }
    .master-box {
        flex: 1;
        min-height: 200px;
    }
    .back-link {
        background: #ffffff;
        color: #3b82f6;
        border: 1.5px solid #bfdbfe;
        padding: 6px 16px;
        border-radius: 20px;
        font-weight: 800;
        font-size: 0.75rem;
        cursor: pointer;
        transition: all 0.2s ease;
        display: flex;
        align-items: center;
    }
    .back-link.btn-new {
        background: #3b82f6;
        color: white;
        border-color: #3b82f6;
    }
    .back-link.btn-new:hover {
        background: #2563eb;
        transform: translateY(-1px);
    }
    .back-link.btn-scan {
        background: #10b981;
        color: white;
        border-color: #10b981;
    }
    .back-link.btn-scan:hover {
        background: #059669;
        transform: translateY(-1px);
    }
    .table-scroll {
        flex: 1;
        overflow: auto;
        border-radius: 0 0 10px 10px;
        position: relative;
    }
    .mes-table {
        width: 100%;
        border-collapse: collapse;
        table-layout: auto;
        min-width: max-content;
    }
    .mes-table th {
        position: sticky;
        top: 0;
        background: #f8fafc;
        padding: 12px;
        font-size: 0.8rem;
        color: #64748b;
        border-bottom: 2px solid #e2e8f0;
        z-index: 5;
        text-align: left;
        white-space: nowrap;
    }
    .mes-table td {
        padding: 12px;
        border-bottom: 1px solid #f1f5f9;
        font-size: 0.9rem;
        cursor: pointer;
        white-space: nowrap;
    }
    .mes-table tr {
        transition: background-color 0.2s;
    }
    .mes-table tr:hover td {
        background: #f8fafc;
    }
    .mes-table tr.selected td {
        background-color: #e0f2fe !important;
    }
    .center {
        text-align: center;
    }
    .bold {
        font-weight: 700;
    }

    .badge {
        padding: 4px 10px;
        border-radius: 12px;
        font-size: 0.75rem;
        font-weight: 700;
        display: inline-block;
    }
    .badge-success {
        background-color: #d1fae5;
        color: #059669;
    }
    .badge-danger {
        background-color: #fee2e2;
        color: #dc2626;
    }
    .badge-warning {
        background-color: #fef3c7;
        color: #d97706;
    }

    .empty-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        height: 100%;
        min-height: 300px;
        color: var(--text-muted, #6c757d);
    }

    .empty-icon {
        font-size: 64px;
        margin-bottom: 16px;
        opacity: 0.3;
    }
    .empty-text {
        font-size: 18px;
        font-weight: 500;
    }
    
    .header-actions {
        display: flex;
        align-items: center;
        gap: 12px;
    }

    /* Custom Dropdown Container */
    .custom-dropdown {
        position: relative;
        width: 320px;
        flex-shrink: 0;
        font-size: 0.75rem;
    }

    /* Trigger Button styling */
    .dropdown-trigger {
        width: 100%;
        height: 32px;
        padding: 0 12px;
        border: 1.5px solid #bfdbfe;
        border-radius: 16px;
        background-color: white;
        display: flex;
        align-items: center;
        justify-content: space-between;
        cursor: pointer;
        transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        outline: none;
        gap: 6px;
    }

    .dropdown-trigger:hover {
        border-color: #3b82f6;
        background-color: #f8fafc;
        box-shadow: 0 2px 4px rgba(59, 130, 246, 0.05);
    }

    .dropdown-trigger.active {
        border-color: #3b82f6;
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
        background-color: white;
    }

    .trigger-content {
        display: flex;
        align-items: center;
        gap: 6px;
        overflow: hidden;
        flex: 1;
    }

    .trigger-label {
        font-weight: 800;
        color: #475569;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        text-align: left;
        flex: 1;
    }

    .trigger-label.placeholder {
        color: #94a3b8;
        font-weight: 600;
    }

    .trigger-arrow {
        color: #94a3b8;
        font-size: 0.65rem;
        transition: transform 0.2s ease;
        flex-shrink: 0;
    }

    .dropdown-trigger.active .trigger-arrow {
        transform: rotate(180deg);
    }

    /* Dropdown Drop Menu */
    .dropdown-menu {
        position: absolute;
        top: calc(100% + 6px);
        right: 0;
        width: 320px;
        background: rgba(255, 255, 255, 0.98);
        backdrop-filter: blur(12px);
        -webkit-backdrop-filter: blur(12px);
        border: 1.5px solid rgba(226, 232, 240, 0.8);
        border-radius: 14px;
        box-shadow:
            0 10px 25px -5px rgba(15, 23, 42, 0.15),
            0 8px 10px -6px rgba(15, 23, 42, 0.1);
        z-index: 1000;
        padding: 6px;
        display: flex;
        flex-direction: column;
        gap: 4px;
        transform-origin: top right;
        animation: slideDown 0.15s cubic-bezier(0.16, 1, 0.3, 1);
    }

    @keyframes slideDown {
        from {
            opacity: 0;
            transform: scale(0.96) translateY(-4px);
        }
        to {
            opacity: 1;
            transform: scale(1) translateY(0);
        }
    }

    /* List styling */
    .menu-list-container {
        max-height: 240px;
        overflow-y: auto;
        display: flex;
        flex-direction: column;
        gap: 3px;
        padding-right: 2px;
    }

    /* Custom scrollbar */
    .menu-list-container::-webkit-scrollbar {
        width: 5px;
    }
    .menu-list-container::-webkit-scrollbar-track {
        background: transparent;
    }
    .menu-list-container::-webkit-scrollbar-thumb {
        background: #cbd5e1;
        border-radius: 3px;
    }
    .menu-list-container::-webkit-scrollbar-thumb:hover {
        background: #94a3b8;
    }

    .no-results {
        padding: 24px 0;
        text-align: center;
        color: #94a3b8;
        font-weight: 500;
        font-size: 0.8rem;
    }

    /* List item button */
    .menu-item {
        width: 100%;
        border: none;
        background: none;
        padding: 8px 12px;
        border-radius: 8px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: space-between;
        text-align: left;
        transition: all 0.15s ease;
        gap: 8px;
    }

    .menu-item:hover {
        background-color: #f1f5f9;
    }

    .menu-item.selected {
        background-color: #eff6ff;
    }

    .item-content {
        display: flex;
        flex-direction: column;
        gap: 2px;
        flex: 1;
        overflow: hidden;
    }

    .trigger-icon-svg {
        color: #3b82f6;
        flex-shrink: 0;
    }

    .item-path {
        font-size: 0.75rem;
        line-height: 1.4;
        word-break: keep-all;
        overflow-wrap: break-word;
    }

    :global(.path-parent) {
        color: #64748b;
        font-weight: 500;
    }

    :global(.path-last) {
        font-weight: 700;
        color: #334155;
    }

    .menu-item.selected :global(.path-last) {
        color: #1d4ed8;
    }

    .item-code {
        font-size: 0.65rem;
        color: #94a3b8;
        font-weight: 500;
    }

    .menu-item.selected .item-code {
        color: #60a5fa;
    }

    .selected-checkmark {
        color: #3b82f6;
        font-size: 1rem;
        font-weight: bold;
        flex-shrink: 0;
    }
</style>
